import { LinearGradient } from "expo-linear-gradient"
import React from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet } from "react-native"

import { useMeasurementPreference } from "@/redux/selectors"
import { AppText, Box, Row, useIsDarkMode } from "@/theme"
import { GRADIENT_HEX_COLORS } from "./heatmap-colors"

const TICK_COUNT = 4 // 0, 1, 2, 3 in (matches HEATMAP_MAX_MM = 3 in)

function buildTicks(isMetric: boolean): string[] {
  return Array.from({ length: TICK_COUNT }, (_, i) => {
    const inches = i
    if (isMetric) {
      return `${Math.round(inches * 25.4)}`
    }
    return `${inches}`
  })
}

export interface IrrigationHeatmapLegendProps {
  /** Override the unit display; defaults to user measurement preference */
  unit?: "in" | "mm"
  /** Override the legend title; defaults to the translated "Application Depth" */
  title?: string
}

export function IrrigationHeatmapLegend({
  unit: unitOverride,
  title: titleOverride,
}: IrrigationHeatmapLegendProps = {}): React.JSX.Element {
  const { t } = useTranslation("fieldIrrigationHistory")
  const isDark = useIsDarkMode()
  const measurementPreference = useMeasurementPreference()
  const isMetric = unitOverride
    ? unitOverride === "mm"
    : measurementPreference === "metric"

  const unit = isMetric ? "mm" : "in"
  const ticks = buildTicks(isMetric)

  const containerBg = isDark
    ? "rgba(24, 24, 24, 0.85)"
    : "rgba(255, 255, 255, 0.88)"
  const textColor = isDark ? "#e0e0e0" : "#222"

  return (
    <Box style={[styles.container, { backgroundColor: containerBg }]}>
      <AppText style={[styles.title, { color: textColor }]}>
        {titleOverride ?? t("heatmapLegendTitle")}
      </AppText>
      <LinearGradient
        colors={GRADIENT_HEX_COLORS as unknown as [string, string, ...string[]]}
        end={{ x: 1, y: 0 }}
        start={{ x: 0, y: 0 }}
        style={styles.gradientBar}
      />
      <Row justifyContent={"space-between"} style={styles.labelRow}>
        {ticks.map((label, i) => (
          <AppText key={i} style={[styles.tick, { color: textColor }]}>
            {i === ticks.length - 1 ? `${label} ${unit}` : label}
          </AppText>
        ))}
      </Row>
    </Box>
  )
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    width: 148,
  },
  gradientBar: {
    borderRadius: 3,
    height: 8,
    marginBottom: 2,
    marginTop: 3,
  },
  labelRow: {
    width: "100%",
  },
  tick: {
    fontSize: 8,
    letterSpacing: 0.2,
  },
  title: {
    fontSize: 9,
    fontWeight: "600",
    letterSpacing: 0.3,
  },
})
